import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import { DataContext } from "../../../context/DataContext";

import { getRol, getToken, getUser } from "../../../api/Account";
import { getCreditsByGestorReportAsync } from "../../../api/Report";
import { getGestoresAsync } from "../../../api/Users";
import { simpleMessage } from "../../../helpers/Helpers";
import { formatearFecha, formatearMonto } from "../../../helpers/formato";
import ReporteAgrupado from "../../../components/reportes/ReporteAgrupado";

const TODOS = "t";
const ORDEN_POR_MONTO = 1;

// Un crédito por fila: sus montos se suman directo, sin `unaVezPorId`.
const dinero = (key, label, total = true) => ({
  key,
  label,
  align: "center",
  total,
  formatoTotal: formatearMonto,
  valor: (c) => formatearMonto(c[key]),
  excel: (c) => c[key],
});

const fecha = (key, label) => ({
  key,
  label,
  align: "center",
  valor: (c) => formatearFecha(c[key]),
  excel: (c) => formatearFecha(c[key]),
});

const COLUMNAS = [
  {
    key: "fullName",
    label: "Cliente",
    align: "left",
    texto: true,
    valor: (c) => c.fullName,
  },
  dinero("montoEntregado", "Principal"),
  dinero("montoPagar", "Monto Pagar"),
  {
    key: "periodicidad",
    label: "Periodicidad",
    align: "center",
    texto: true,
    valor: (c) => c.periodicidad,
  },
  {
    key: "plazo",
    label: "Plazo",
    align: "center",
    texto: true,
    valor: (c) => c.plazo,
  },
  fecha("fechaEntrega", "F.Entrega"),
  fecha("fechaVencimiento", "F.Vencimiento"),
  dinero("saldoPrincipal", "S.Capital"),
  dinero("saldoInteresC", "S.Interes"),
  dinero("saldoTotal", "S.Total"),
];

const CreditsByGestor = () => {
  const [data, setData] = useState([]);
  const [gestorName, setGestorName] = useState("Todos");

  const { selectedGestor, selectedSortBy } = useParams();
  const { setIsLoading, setIsUnautorized, title, slogan, icon } =
    useContext(DataContext);

  const token = getToken();
  const user = getUser();
  const rol = getRol();

  const manejarError = useCallback(
    (result) => {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      simpleMessage(result.error, "error");
    },
    [setIsUnautorized]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const [reporte, gestores] = await Promise.all([
        getCreditsByGestorReportAsync(token, {
          selectedSortBy: parseInt(selectedSortBy),
          gestorId: selectedGestor === TODOS ? 0 : parseInt(selectedGestor),
        }),
        selectedGestor === TODOS ? null : getGestoresAsync(token),
      ]);
      setIsLoading(false);

      if (!reporte.statusResponse) {
        manejarError(reporte);
        return;
      }
      setData(reporte.data.result);

      if (selectedGestor !== TODOS) {
        // Si la lista de gestores falla o no lo trae (dado de baja), el nombre sale del reporte.
        const gestor =
          gestores && gestores.statusResponse
            ? gestores.data.result.find((g) => g.id.toString() === selectedGestor)
            : null;
        setGestorName(
          gestor ? gestor.fullName : reporte.data.result[0]?.gestorNombre ?? selectedGestor
        );
      }
    })();
  }, [token, selectedGestor, selectedSortBy, setIsLoading, manejarError]);

  // El servidor ya manda el nombre: también el de un gestor dado de baja.
  const agruparPor = useCallback((fila) => fila.gestorNombre, []);

  return (
    <ReporteAgrupado
      tituloReporte="Reporte de Créditos"
      nombreArchivo="Creditos"
      parametros={[{ label: "Gestor Seleccionado", valor: gestorName }]}
      columnas={COLUMNAS}
      data={data}
      agruparPor={agruparPor}
      ordenInicial={
        parseInt(selectedSortBy) === ORDEN_POR_MONTO ? "montoEntregado" : "fullName"
      }
      contexto={{ title, slogan, icon, user, rol }}
    />
  );
};

export default CreditsByGestor;
